import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { usePage } from '@inertiajs/react'

import useMutation from '@hooks/useGQLMutation'
import { SendEmailDocument } from '@frontend/graphql/mutations/sendEmail.generated'
import { useAlertsStore } from '@stores/alertsStore'
import { toast } from '@stores/toastStore'

import { FormDialog } from '@components/molecules/FormDialog'
import { RichTextEditorField } from '@components/molecules/RichTextEditorField'

import { i18nValidation, scopedTranslation } from '@frontend/utils/I18n'
import TextInput from '@atoms/form/TextInput'
import type { SendEmailDialogState } from '../../types/dialogTypes'
import type { SharedInertiaProps } from '../../types/groupTypes'

const t = scopedTranslation('components.send_email_dialog')
const tAttributes = scopedTranslation('attributes.email')

const ALERTS_PATH = 'sendEmail' as const

const createSchema = (showReplyTo: boolean) => yup.object({
  emailSubject: yup
    .string()
    .label(tAttributes('subject'))
    .required(i18nValidation('required')),
  senderName: yup
    .string()
    .label(tAttributes('sender_name'))
    .required(i18nValidation('required')),
  body: yup
    .string()
    .label(tAttributes('body'))
    .required(i18nValidation('required')),
  replyTo: yup
    .string()
    .label(tAttributes('reply_to'))
    .email(i18nValidation('valid'))
    .when([], {
      is: () => showReplyTo,
      then: schema => schema.required(i18nValidation('required'))
    })
}).required()

type FormData = {
  emailSubject: string
  senderName: string
  body: string
  replyTo?: string
}

type SendEmailDialogProps = {
  onClose: () => void
  dialogState: SendEmailDialogState
}

export function SendEmailDialog({
  onClose,
  dialogState,
}: SendEmailDialogProps) {
  const { dialogData } = dialogState
  const supporterId = dialogData.id
  const supporterName = `${dialogData.firstName || ''} ${dialogData.lastName || ''}`.trim()
  const groupId = dialogData.groupId
  
  const { props: { permissions } } = usePage<SharedInertiaProps>()
  const showReplyTo = !permissions?.emailInboxEnabled

  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset
  } = useForm<FormData>({
    resolver: yupResolver(createSchema(showReplyTo)),
    reValidateMode: 'onBlur'
  })

  const sendEmailMutationHook = useMutation(SendEmailDocument)

  const onSubmit = (data: FormData) => {
    clearAlerts(ALERTS_PATH)
    
    const mutationData = {
      supporterId,
      groupId,
      emailBody: data.body,
      emailSubject: data.emailSubject,
      senderName: data.senderName,
      ...(showReplyTo && data.replyTo && { replyTo: data.replyTo })
    }

    sendEmailMutationHook.mutate(mutationData, {
      onSuccess: (response) => {
        if (response?.sendEmail?.success) {
          reset()
          clearAlerts(ALERTS_PATH)
          onClose()
          toast.success(t('alerts.success.title'))
        } else {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: response?.sendEmail?.errors?.map(error => ({
              type: 'error',
              details: {
                title: error.message
              }
            })) || []
          })
        }
      },
      onError: () => {
        setAlerts({
          alertsPath: ALERTS_PATH,
          alerts: [{
            type: 'error',
            details: {
              title: t('errors.send_failed')
            }
          }]
        })
      }
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  return (
    <FormDialog.Root 
      open={true}
      onOpenChange={handleOpenChange}
      testId="send-email-dialog"
      preventOutsideClick
      hasTinyMCE={true}
    >
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title', { name: supporterName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Body>
          <TextInput
            label={tAttributes('subject')}
            errors={errors}
            placeholder={t('form.placeholder.subject')}
            {...register('emailSubject')}
            required={true}
          />
          <TextInput
            label={tAttributes('sender_name')}
            errors={errors}
            placeholder={t('form.placeholder.sender_name')}
            {...register('senderName')}
            required={true}
          />
          {showReplyTo && (
            <TextInput
              label={tAttributes('reply_to')}
              errors={errors}
              placeholder={t('form.placeholder.reply_to')}
              {...register('replyTo')}
              required={true}
            />
          )}
          <Controller
            name="body"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <RichTextEditorField
                label={tAttributes('body')}
                placeholder={t('form.placeholder.body')}
                errors={errors}
                required={true}
                {...field}
              />
            )}
          />
        </FormDialog.Body>
        <FormDialog.Footer 
          submitText={t('form.send_now')} 
          isLoading={sendEmailMutationHook.isLoading}
        />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
